/** UI strings for the F07 models (wave function, two-state superposition); merged into the photons dictionary. */
export const sv = {
  wfTitle: "Vågfunktion: amplitud, sannolikhetstäthet och normering",
  wfNote:
    "En reell vågfunktion i en dimension. Övre axeln visar amplituden, undre axeln sannolikhetstätheten. Axlarna är skalade för faktorn ±2, så en ändrad faktor syns som en ändrad höjd. Sannolikheten i intervallet är kvoten mellan intervallets integral och hela integralen och beror därför varken på faktorn eller på normeringen.",
  wfShape: "Funktion",
  wfBox: "sin(nπx/L) i låda",
  wfGauss: "Gauss e^(−x²/2a²)",
  wfExponential: "(x/a)e^(−x/a)",
  wfDouble: "e^(−x/a)(1 − e^(−x/a))",
  wfLevel: "Kvanttal n",
  wfWidthBox: "Lådans bredd L (nm)",
  wfWidthA: "Breddparameter a (nm)",
  wfFactor: "Faktor c framför funktionen",
  wfForm: "Ritad funktion",
  wfNormalized: "Normerad ψ",
  wfRaw: "Rå form f",
  wfLowerBox: "Intervallets början x₁/L",
  wfUpperBox: "Intervallets slut x₂/L",
  wfLowerA: "Intervallets början x₁/a",
  wfUpperA: "Intervallets slut x₂/a",
  wfSvg: "Amplitud och sannolikhetstäthet",
  wfDesc:
    "Övre diagram: den ritade funktionen mot x med nollinjen markerad. Undre diagram: dess kvadrat mot x. Det skuggade området är integralen över det valda intervallet. Streckad lodrät linje: väntevärdet. Prickad lodrät linje: typvärdet.",
  wfAxisX: "x (nm)",
  wfAxisPsi: "cψ (nm^(−1/2))",
  wfAxisRaw: "cf",
  wfAxisDensity: "|cψ|² (nm⁻¹)",
  wfAxisRawDensity: "|cf|²",
  wfNormConstant: "Normeringskonstant N i ψ = N f (nm^(−1/2))",
  wfDeckConstant: "C i ψ = Cxe^(−x/a) (nm^(−3/2))",
  wfTotalNormalized: "∫|cψ|² dx över alla x",
  wfTotalRaw: "∫|cf|² dx över alla x (nm)",
  wfIntervalNormalized: "∫|cψ|² dx över intervallet",
  wfIntervalRaw: "∫|cf|² dx över intervallet (nm)",
  wfProbability: "Sannolikhet i intervallet (kvot)",
  wfMode: "Typvärde, största täthet (nm)",
  wfMean: "Väntevärde ⟨x⟩ (nm)",
  wfSpread: "Δx (nm)",
  wfDeltaP: "Δp (ħ/nm)",
  wfProduct: "ΔxΔp/ħ",
  wfPeak: "Största värdet av normerad |ψ|² (nm⁻¹)",
  wfKinetic: "⟨K⟩ för en elektron (eV)",
  wfFormulaBox: String.raw`$\psi_n=\sqrt{2/L}\,\sin(n\pi x/L)$ för $0<x<L$ (40.35). $\langle K\rangle=E_n=n^2\pi^2\hbar^2/(2mL^2)$ (40.31). Elektronmassa och $\hbar$ enligt CODATA.`,
  wfFormulaGauss: String.raw`$\psi=(a\sqrt{\pi})^{-1/2}e^{-x^2/(2a^2)}$, $\Delta x=a/\sqrt2$, $\Delta p=\hbar/(\sqrt2\,a)$, $\langle K\rangle=\hbar^2/(4ma^2)$. Tentan HT25-7 skriver bredden $A$.`,
  wfFormulaExponential: String.raw`$\psi=Cxe^{-x/a}$ för $x\ge0$, $C=2/a^{3/2}$, $\langle x\rangle=3a/2$, typvärde $a$, $\langle K\rangle=\hbar^2/(2ma^2)$. Vid $a=1$ är detta HT22-6.`,
  wfFormulaDouble: String.raw`$f=e^{-x/a}(1-e^{-x/a})$ för $x\ge0$: $\int f^2dx=a/12$, typvärde $a\ln2$, $\langle x\rangle=13a/12$, $\langle K\rangle=\hbar^2/(ma^2)$. Vid $a=1$ är detta HT24-7.`,
  spTitle: "Två stationära tillstånd i en oändlig potentialbrunn",
  spNote:
    "Ψ = e^(iγ)[cos α ψ₁e^(−iE₁t/ħ) + e^(iφ) sin α ψₙe^(−iEₙt/ħ)] för en elektron i en låda med bredden L. Tiden anges i perioden T = 2πħ/(Eₙ − E₁). Den globala fasen γ ändrar real- och imaginärdel men ingenting som kan mätas. Den inkoherenta blandningen har samma vikter men inga korstermer.",
  spViewWave: "Vågfunktion och ström",
  spViewTrack: "Väntevärde över tiden",
  spState: "Tillstånd",
  spCoherent: "Koherent superposition",
  spIncoherent: "Inkoherent blandning",
  spInterval: "Intervall",
  spLeft: "Vänstra halvan",
  spMiddle: "Mittersta halvan",
  spPartner: "Partnernivå n",
  spMixing: "Populationsvinkel α (°)",
  spPhase: "Relativ fas φ vid t = 0 (°)",
  spTime: "Tid t/T",
  spGlobal: "Global fas γ (°)",
  spLength: "Lådans bredd L (nm)",
  spWaveSvg: "Vågfunktion, sannolikhetstäthet och sannolikhetsström",
  spWaveDesc:
    "Överst: real- och imaginärdel av Ψ mot x. I mitten: sannolikhetstätheten med det valda intervallet skuggat och väntevärdet som streckad linje. Nederst: sannolikhetsströmmen j mot x; positiv ström går åt höger.",
  spTrackSvg: "Väntevärde och intervallsannolikhet mot tiden",
  spTrackDesc:
    "Överst: väntevärdet ⟨x⟩/L mot t/T över två perioder. Nederst: sannolikheten i det valda intervallet mot t/T. Punkterna markerar den valda tiden.",
  spIncoherentNote:
    "En inkoherent blandning har ingen gemensam vågfunktion; bara tätheten (vikterna gånger |ψ₁|² och |ψₙ|²) ritas. Strömmen är noll.",
  spAxisX: "x/L",
  spAxisPsi: "Ψ (L^(−1/2))",
  spAxisDensity: "|Ψ|² (1/L)",
  spAxisCurrent: "j (fs⁻¹)",
  spAxisTime: "t/T",
  spAxisMean: "⟨x⟩/L",
  spAxisProbability: "P",
  spRe: "Re Ψ",
  spIm: "Im Ψ",
  spE1: "E₁ (eV)",
  spEn: "Eₙ (eV)",
  spOutcomes: "Möjliga energimätvärden (eV)",
  spWeights: "Sannolikheter cos²α; sin²α",
  spMeanEnergy: "⟨E⟩ = cos²α E₁ + sin²α Eₙ (eV)",
  spPeriod: "Period T = 2πħ/(Eₙ − E₁) (fs)",
  spTheta: "θ = (Eₙ − E₁)t/ħ − φ (°)",
  spMeanX: "⟨x⟩ (nm)",
  spMeanRange: "⟨x⟩ varierar mellan (nm)",
  spVelocity: "d⟨x⟩/dt (m/s)",
  spMomentum: "⟨p⟩ = m d⟨x⟩/dt (kg m/s)",
  spProbability: "Sannolikhet i intervallet",
  spCenterCurrent: "Ström genom mitten j(L/2) (fs⁻¹)",
  spFormula: String.raw`$\theta=(E_n-E_1)t/\hbar-\varphi$. $|\Psi|^2=\cos^2\!\alpha\,\psi_1^2+\sin^2\!\alpha\,\psi_n^2+\sin2\alpha\,\psi_1\psi_n\cos\theta$, $j=\dfrac{\hbar}{m}\,\mathrm{Im}\!\left(\Psi^*\dfrac{\partial\Psi}{\partial x}\right)$, $\dfrac{\partial|\Psi|^2}{\partial t}+\dfrac{\partial j}{\partial x}=0$. Elektronmassa och $\hbar$ enligt CODATA.`,
};
export const en: Record<keyof typeof sv, string> = {
  wfTitle: "Wave function: amplitude, probability density and normalization",
  wfNote:
    "One real wave function in one dimension. The upper axis shows the amplitude, the lower axis the probability density. The axes are scaled for the factor ±2, so a changed factor shows as a changed height. The probability in the interval is the ratio of the interval's integral to the whole integral, so it depends neither on the factor nor on the normalization.",
  wfShape: "Function",
  wfBox: "sin(nπx/L) in a box",
  wfGauss: "Gaussian e^(−x²/2a²)",
  wfExponential: "(x/a)e^(−x/a)",
  wfDouble: "e^(−x/a)(1 − e^(−x/a))",
  wfLevel: "Quantum number n",
  wfWidthBox: "Box width L (nm)",
  wfWidthA: "Width parameter a (nm)",
  wfFactor: "Factor c in front of the function",
  wfForm: "Drawn function",
  wfNormalized: "Normalized ψ",
  wfRaw: "Raw form f",
  wfLowerBox: "Interval start x₁/L",
  wfUpperBox: "Interval end x₂/L",
  wfLowerA: "Interval start x₁/a",
  wfUpperA: "Interval end x₂/a",
  wfSvg: "Amplitude and probability density",
  wfDesc:
    "Upper plot: the drawn function against x with the zero line marked. Lower plot: its square against x. The shaded area is the integral over the chosen interval. Dashed vertical line: the expectation value. Dotted vertical line: the most probable position.",
  wfAxisX: "x (nm)",
  wfAxisPsi: "cψ (nm^(−1/2))",
  wfAxisRaw: "cf",
  wfAxisDensity: "|cψ|² (nm⁻¹)",
  wfAxisRawDensity: "|cf|²",
  wfNormConstant: "Normalization constant N in ψ = N f (nm^(−1/2))",
  wfDeckConstant: "C in ψ = Cxe^(−x/a) (nm^(−3/2))",
  wfTotalNormalized: "∫|cψ|² dx over all x",
  wfTotalRaw: "∫|cf|² dx over all x (nm)",
  wfIntervalNormalized: "∫|cψ|² dx over the interval",
  wfIntervalRaw: "∫|cf|² dx over the interval (nm)",
  wfProbability: "Probability in the interval (ratio)",
  wfMode: "Most probable position, highest density (nm)",
  wfMean: "Expectation value ⟨x⟩ (nm)",
  wfSpread: "Δx (nm)",
  wfDeltaP: "Δp (ħ/nm)",
  wfProduct: "ΔxΔp/ħ",
  wfPeak: "Largest value of the normalized |ψ|² (nm⁻¹)",
  wfKinetic: "⟨K⟩ for an electron (eV)",
  wfFormulaBox: String.raw`$\psi_n=\sqrt{2/L}\,\sin(n\pi x/L)$ for $0<x<L$ (40.35). $\langle K\rangle=E_n=n^2\pi^2\hbar^2/(2mL^2)$ (40.31). Electron mass and $\hbar$ from CODATA.`,
  wfFormulaGauss: String.raw`$\psi=(a\sqrt{\pi})^{-1/2}e^{-x^2/(2a^2)}$, $\Delta x=a/\sqrt2$, $\Delta p=\hbar/(\sqrt2\,a)$, $\langle K\rangle=\hbar^2/(4ma^2)$. Exam HT25-7 writes the width $A$.`,
  wfFormulaExponential: String.raw`$\psi=Cxe^{-x/a}$ for $x\ge0$, $C=2/a^{3/2}$, $\langle x\rangle=3a/2$, most probable position $a$, $\langle K\rangle=\hbar^2/(2ma^2)$. At $a=1$ this is HT22-6.`,
  wfFormulaDouble: String.raw`$f=e^{-x/a}(1-e^{-x/a})$ for $x\ge0$: $\int f^2dx=a/12$, most probable position $a\ln2$, $\langle x\rangle=13a/12$, $\langle K\rangle=\hbar^2/(ma^2)$. At $a=1$ this is HT24-7.`,
  spTitle: "Two stationary states of an infinite square well",
  spNote:
    "Ψ = e^(iγ)[cos α ψ₁e^(−iE₁t/ħ) + e^(iφ) sin α ψₙe^(−iEₙt/ħ)] for an electron in a box of width L. Time is given in the period T = 2πħ/(Eₙ − E₁). The global phase γ changes the real and imaginary parts but nothing measurable. The incoherent mixture has the same weights but no cross terms.",
  spViewWave: "Wave function and current",
  spViewTrack: "Expectation value over time",
  spState: "State",
  spCoherent: "Coherent superposition",
  spIncoherent: "Incoherent mixture",
  spInterval: "Interval",
  spLeft: "Left half",
  spMiddle: "Middle half",
  spPartner: "Partner level n",
  spMixing: "Population angle α (°)",
  spPhase: "Relative phase φ at t = 0 (°)",
  spTime: "Time t/T",
  spGlobal: "Global phase γ (°)",
  spLength: "Box width L (nm)",
  spWaveSvg: "Wave function, probability density and probability current",
  spWaveDesc:
    "Top: real and imaginary parts of Ψ against x. Middle: the probability density with the chosen interval shaded and the expectation value as a dashed line. Bottom: the probability current j against x; positive current flows to the right.",
  spTrackSvg: "Expectation value and interval probability against time",
  spTrackDesc:
    "Top: the expectation value ⟨x⟩/L against t/T over two periods. Bottom: the probability in the chosen interval against t/T. The dots mark the chosen time.",
  spIncoherentNote:
    "An incoherent mixture has no common wave function; only the density (the weights times |ψ₁|² and |ψₙ|²) is drawn. The current is zero.",
  spAxisX: "x/L",
  spAxisPsi: "Ψ (L^(−1/2))",
  spAxisDensity: "|Ψ|² (1/L)",
  spAxisCurrent: "j (fs⁻¹)",
  spAxisTime: "t/T",
  spAxisMean: "⟨x⟩/L",
  spAxisProbability: "P",
  spRe: "Re Ψ",
  spIm: "Im Ψ",
  spE1: "E₁ (eV)",
  spEn: "Eₙ (eV)",
  spOutcomes: "Possible energy measurement results (eV)",
  spWeights: "Probabilities cos²α; sin²α",
  spMeanEnergy: "⟨E⟩ = cos²α E₁ + sin²α Eₙ (eV)",
  spPeriod: "Period T = 2πħ/(Eₙ − E₁) (fs)",
  spTheta: "θ = (Eₙ − E₁)t/ħ − φ (°)",
  spMeanX: "⟨x⟩ (nm)",
  spMeanRange: "⟨x⟩ varies between (nm)",
  spVelocity: "d⟨x⟩/dt (m/s)",
  spMomentum: "⟨p⟩ = m d⟨x⟩/dt (kg m/s)",
  spProbability: "Probability in the interval",
  spCenterCurrent: "Current through the middle j(L/2) (fs⁻¹)",
  spFormula: String.raw`$\theta=(E_n-E_1)t/\hbar-\varphi$. $|\Psi|^2=\cos^2\!\alpha\,\psi_1^2+\sin^2\!\alpha\,\psi_n^2+\sin2\alpha\,\psi_1\psi_n\cos\theta$, $j=\dfrac{\hbar}{m}\,\mathrm{Im}\!\left(\Psi^*\dfrac{\partial\Psi}{\partial x}\right)$, $\dfrac{\partial|\Psi|^2}{\partial t}+\dfrac{\partial j}{\partial x}=0$. Electron mass and $\hbar$ from CODATA.`,
};
